/**
 * @file useAdminAuth.ts
 * @description Custom hook to check if current user has admin role
 * @author XiaVan Team
 * 
 * Usage:
 * const { isAdmin, loading } = useAdminAuth()
 */

import { useState, useEffect } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { getUserRole } from '@/lib/api-admin'

/**
 * Hook return type
 */
interface UseAdminAuthReturn {
  isAdmin: boolean
  loading: boolean
}

/**
 * Custom hook to verify if current user has admin role
 * 
 * Checks Firestore users collection for role field
 * Returns loading state while fetching
 * 
 * @returns {UseAdminAuthReturn} Object with isAdmin flag and loading state
 * 
 * @example
 * const { isAdmin, loading } = useAdminAuth()
 * 
 * if (loading) return <LoadingSpinner />
 * if (!isAdmin) return <Redirect to="/" />
 * return <AdminPanel />
 */
export function useAdminAuth(): UseAdminAuthReturn {
  const { user, loading: authLoading } = useAuth()
  const [isAdmin, setIsAdmin] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    /**
     * Fetch user role from Firestore
     */
    async function checkAdminRole() {
      if (!user) {
        setIsAdmin(false)
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        const role = await getUserRole(user.uid)
        setIsAdmin(role === 'admin')
      } catch (error) {
        console.error('Error checking admin role:', error)
        setIsAdmin(false)
      } finally {
        setLoading(false)
      }
    }

    // Wait for auth to finish loading before checking role
    if (!authLoading) {
      checkAdminRole()
    }
  }, [user, authLoading])

  return {
    isAdmin,
    loading: authLoading || loading,
  }
}
